import React from 'react';
import { Camera, X } from 'lucide-react';
import Button from '../shared/Button';
import Input from '../shared/Input';
import SearchSelect from '../shared/SearchSelect';
import ImageBrowser from '../shared/ImageBrowser';

/** Same bucket as Photo Gallery / max_images — excludes portfolio uploads */
const NON_PORTFOLIO_IMAGE_QUERY = { non_portfolio: true };

const ServiceModal = ({
  isOpen,
  isEdit,
  formData,
  categories,
  onClose,
  onSubmit,
  onInputChange,
  isLoading,
  onImageSelect,
  maxImages = null
}) => {
  const [showImageBrowser, setShowImageBrowser] = React.useState(false);
  
  if (!isOpen) return null;
  
  const handleImageSelect = (image) => {
    if (image && onImageSelect) {
      onImageSelect(image);
      setShowImageBrowser(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white m-4">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {isEdit ? 'Edit Service' : 'Create New Service'}
          </h3>
          
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Name *
                </label>
                <Input
                  type="text"
                  value={formData.service_name}
                  onChange={(e) => onInputChange('service_name', e.target.value)}
                  placeholder="Enter service name"
                  required
                />
              </div>
              
              {/* Service Image */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Service Image
                </label>
                <div className="flex items-center gap-4">
                  {formData.image_url ? (
                    <div className="relative">
                      <img
                        src={formData.image_url}
                        alt="Service"
                        className="w-24 h-24 rounded-lg object-cover border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => onInputChange('image_url', '')}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <Button
                    type="button"
                    onClick={() => setShowImageBrowser(true)}
                    variant="outline"
                    size="sm"
                    icon={Camera}
                  >
                    {formData.image_url ? 'Change Image' : 'Select Image'}
                  </Button>
                </div>
              </div>
              
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <SearchSelect
                  value={formData.service_category_id}
                  onChange={(value) => onInputChange('service_category_id', value)}
                  options={categories.map(category => ({
                    value: category.id,
                    label: category.category_name
                  }))}
                  placeholder="Select a category"
                  required
                />
              </div>
              
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => onInputChange('description', e.target.value)}
                  placeholder="Describe your service in detail"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  required
                />
              </div>
              
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Tags
                </label>
                <Input
                  type="text"
                  value={formData.search_tags}
                  onChange={(e) => onInputChange('search_tags', e.target.value)}
                  placeholder="Enter search tags (comma-separated, e.g., wedding, photography, event)"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add keywords to help customers find your service more easily
                </p>
              </div>
              
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Starting Price ($) *
                </label>
                <Input
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => onInputChange('base_price', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  hideSpinner={true}
                  preventTextInput={true}
                  formatNumber={true}
                  decimalPlaces={2}
                  required
                />
              </div>
              
              <div className="sm:col-span-2 hidden">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => onInputChange('is_featured', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Featured Service</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Featured services appear prominently in search results
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
              <Button
                type="button"
                onClick={onClose}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                loading={isLoading}
                className="w-full sm:w-auto"
              >
                {isEdit ? 'Update Service' : 'Create Service'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Image Browser Modal */}
      {showImageBrowser && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
            onClick={() => setShowImageBrowser(false)}
          ></div>
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Select Service Image</h3>
                <button
                  onClick={() => setShowImageBrowser(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-xs text-gray-500 mb-3">
                  Shows <strong>general</strong> uploads only (portfolio images are on the Portfolio page). Limits follow your plan&apos;s{' '}
                  <strong>max uploaded images</strong> cap.
                  {maxImages === null ? ' Current plan: unlimited.' : ` Current plan: up to ${maxImages} general images.`}
                </p>
                <ImageBrowser
                  listQueryParams={NON_PORTFOLIO_IMAGE_QUERY}
                  uploadImageType="general"
                  showUpload
                  showDelete
                  showSetDefault={false}
                  maxImages={maxImages}
                  onSelectImage={handleImageSelect}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceModal;
